import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject } from 'rxjs';
import find from 'lodash-es/find';

import { CompanyService } from '@app/shared/services/company.service';
import { IFBLocaleOverride, IFBMapWithLocaleKeys } from 'interfaces';

export interface IFBTranslatable {
  key: string;
  params?: any;
}

@Injectable()
export class TranslateService {
  private _locale: BehaviorSubject<string> = new BehaviorSubject('');

  constructor(
    private companyService: CompanyService,
    private translocoService: TranslocoService
  ) {}

  get locale(): string {
    return this._locale.getValue();
  }

  /**
   * Returns the original language used
   */
  public getLocale(short: boolean = false): string {
    const locale = this.getRawLocale();
    const reversed = this.reverseLocale(locale);

    if (short) {
      return this.shortenLocale(reversed);
    } else {
      return reversed;
    }
  }

  /**
   * @Deprecated use locale instead. Clean up after merged with dev.
   */
  public getRawLocale() {
    return this.locale;
  }

  /**
   * Sets the locale to use when loading translation files. The provided one might be overriden by company settings.
   */
  setLocale = (preferredLocale: string): string => {
    const enabledLocale = this.getEnabledLocale(preferredLocale);
    const overridenLocale = this.overrideLocale(enabledLocale);
    this._locale.next(overridenLocale);

    this.translocoService.setActiveLang(this.locale);

    return this.locale;
  };

  public getEnabledLocale(preferredLocale: string): string {
    const languages = this.companyService.data.languages;
    const enabledLocales = (languages && languages.enabledLocales) || [];

    return (
      find(enabledLocales, (enabledLocale: string) => {
        return this.shortenLocale(preferredLocale) === this.shortenLocale(enabledLocale);
      }) || languages.defaultLocale
    );
  }

  public shortenLocale(locale = ''): string {
    return locale.substr(0, 2).toUpperCase();
  }

  /**
   * Given a preferedLocale it will either override it if the company language settings say so, or return the preferedLocale if not.
   */
  public overrideLocale(preferedLocale: string): string {
    const overrides = (this.companyService.data.languages && this.companyService.data.languages.localeOverrides) || [];
    const localeOverride = overrides && find(overrides, { originalLocale: preferedLocale });

    return localeOverride ? (localeOverride as IFBLocaleOverride).replacingLocale : preferedLocale;
  }

  /**
   * A reverse mapping of localeOverride
   */
  public reverseLocale(translateLocale: string): string {
    const overrides = this.companyService.data.languages.localeOverrides || [];
    const localeOverride = overrides && find(overrides, { replacingLocale: translateLocale });

    return localeOverride ? (localeOverride as IFBLocaleOverride).originalLocale : translateLocale;
  }

  // Check tests for details
  public overrideLocaleKeys(objectWithKeysLocales: IFBMapWithLocaleKeys<any>) {
    let key;
    const result: IFBMapWithLocaleKeys<any> = {};

    for (key in objectWithKeysLocales) {
      if (objectWithKeysLocales.hasOwnProperty(key)) {
        result[this.overrideLocale(key)] = objectWithKeysLocales[key];
      }
    }

    return result;
  }

  // Check tests for details
  public reverseLocaleKeys(objectWithKeysLocales: IFBMapWithLocaleKeys<any>) {
    let key;
    const result: IFBMapWithLocaleKeys<any> = {};

    for (key in objectWithKeysLocales) {
      if (objectWithKeysLocales.hasOwnProperty(key)) {
        result[this.reverseLocale(key)] = objectWithKeysLocales[key];
      }
    }

    return result;
  }

  public getLocalizedConstantString(
    source: IFBMapWithLocaleKeys<string>,
    fallbackLocale: string = 'EN',
    short: boolean = true
  ): string {
    if (!source) {
      return '';
    }

    let preferredLanguage = this.getLocale(short);
    if (!source[preferredLanguage]) {
      preferredLanguage = fallbackLocale;
    }
    return source[preferredLanguage] || '';
  }
}
